import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface UseTableScrollProps {
  cellWidth: number;
  maxRows?: number;
}

interface ScrollDirections {
  bottom: boolean;
  left: boolean;
  right: boolean;
  top: boolean;
}

const MOBILE_BREAKPOINT = 600;

export const useTableScroll = ({ cellWidth, maxRows }: UseTableScrollProps) => {
  const scrollRef = useRef<HTMLTableElement>(null);
  const [currentTransform, setCurrentTransform] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const [tableStructure, setTableStructure] = useState<{
    headings: HTMLCollectionOf<HTMLTableCellElement> | null;
    pinCount: number;
    rowCount: number;
  }>({
    pinCount: 0,
    rowCount: 0,
    headings: null,
  });

  const [scrollDirection, setScrollDirection] = useState<ScrollDirections>({
    top: false,
    bottom: false,
    left: false,
    right: false,
  });

  const shouldShowScrollButtons = useMemo(
    () => scrollDirection.left || scrollDirection.right,
    [scrollDirection.left, scrollDirection.right],
  );

  const getTableElements = useCallback(() => {
    if (!scrollRef.current) return null;

    const table = scrollRef.current.querySelector('table');
    const head = scrollRef.current.getElementsByTagName('thead')[0];
    const tbody = scrollRef.current.getElementsByTagName('tbody')[0];
    const tfoot = scrollRef.current.getElementsByTagName('tfoot')[0];

    if (!table || !head) return null;

    const headings = head.getElementsByTagName('th');
    if (!headings.length) return null;

    // `container` is returned rather than re-read from the ref by each caller: this function has
    // already proved it is non-null, and handing it back is what lets that narrowing reach them.
    return { container: scrollRef.current, table, headings, tbody, tfoot };
  }, []);

  const updateTableStructure = useCallback(() => {
    const elements = getTableElements();
    if (!elements) return;

    const { headings, tbody } = elements;
    const rowCount = tbody?.getElementsByTagName('tr')?.length || 0;
    const pinCount = isMobile ? 0 : [...headings].filter(h => h.dataset.pinned === 'true').length;

    setTableStructure({
      pinCount,
      rowCount,
      headings,
    });
  }, [getTableElements, isMobile]);

  const checkHorizontalScroll = useCallback(() => {
    const elements = getTableElements();
    if (!elements) return { left: false, right: false };

    const { container, table } = elements;
    const containerWidth = container.clientWidth;
    const tableWidth = table.clientWidth;

    const isAtStart = currentTransform >= 0;
    const isAtEnd = currentTransform <= -(tableWidth - containerWidth);

    return {
      left: !isAtStart,
      right: !isAtEnd && tableWidth > containerWidth,
    };
  }, [currentTransform, getTableElements]);

  const checkVerticalScroll = useCallback(() => {
    if (!tableStructure.headings) {
      return { top: false, bottom: false };
    }

    const elements = getTableElements();
    if (!elements || !elements.tbody || !elements.tfoot) {
      return { top: false, bottom: false };
    }

    const { tbody, tfoot } = elements;
    const headingBottom = tableStructure.headings[0].getBoundingClientRect().bottom;
    const footerTop = tfoot.getBoundingClientRect().top;
    const bodyTop = tbody.getBoundingClientRect().top;
    const bodyBottom = tbody.getBoundingClientRect().bottom;

    return {
      top: bodyTop < headingBottom,
      bottom: bodyBottom > footerTop,
    };
  }, [getTableElements, tableStructure.headings]);

  const checkScroll = useCallback(
    (shouldCheckVerticalScroll = false) => {
      const horizontal = checkHorizontalScroll();

      if (shouldCheckVerticalScroll) {
        const vertical = checkVerticalScroll();
        setScrollDirection({
          ...horizontal,
          ...vertical,
        });
      } else {
        setScrollDirection(prev => ({
          ...prev,
          ...horizontal,
        }));
      }
    },
    [checkHorizontalScroll, checkVerticalScroll],
  );

  const onScrollClick = useCallback(
    (direction: 'left' | 'right') => {
      const elements = getTableElements();
      if (!elements) return;

      const { container, table } = elements;
      const containerWidth = container.clientWidth;
      const tableWidth = table.clientWidth;
      const maxScroll = Math.max(0, tableWidth - containerWidth);

      const newTransform =
        direction === 'left'
          ? Math.min(0, currentTransform + cellWidth)
          : Math.max(-maxScroll, currentTransform - cellWidth);

      setCurrentTransform(newTransform);
      checkScroll();
    },
    [cellWidth, checkScroll, currentTransform, getTableElements],
  );

  useEffect(() => {
    setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);

    updateTableStructure();

    const shouldCheckVerticalScroll = !!maxRows && !!tableStructure.rowCount && maxRows < tableStructure.rowCount;

    checkScroll(shouldCheckVerticalScroll);

    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
      updateTableStructure();
      checkScroll(!!maxRows);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [maxRows, checkScroll, updateTableStructure, tableStructure.rowCount]);

  return {
    scrollRef,
    currentTransform,
    scrollDirection,
    shouldShowScrollButtons,
    pinCount: tableStructure.pinCount,
    onScrollClick,
    checkScroll,
    rowCount: tableStructure.rowCount,
  };
};
